// CG Pricing — series filter tabs. Groups are server-rendered; tabs only
// toggle [hidden]. The active filter is mirrored to the URL hash so a
// filtered view survives reload/share; a matching hash on load re-applies it.
class CgPricing extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[data-cg-filter]'));
    this.groups = Array.from(this.querySelectorAll('[data-cg-series]'));
    this.scroller = this.querySelector('.cg-pricing__tabs');
    // Element-scoped, so no teardown needed; re-adding the same handler is a no-op.
    this.addEventListener('click', this.onSwatchClick);

    // If the default variant's color value carries no admin swatch, the
    // server marks nothing pressed — fall back to checking the first
    // rendered swatch so an interactive card always shows one selection.
    this.querySelectorAll('.cg-pricing__card').forEach((card) => {
      const buttons = Array.from(card.querySelectorAll('[data-cg-swatch]'));
      if (buttons.length && !buttons.some((b) => b.getAttribute('aria-pressed') === 'true')) {
        buttons[0].setAttribute('aria-pressed', 'true');
      }
    });

    if (!this.tabs.length) return;

    this.tabs.forEach((tab) => tab.addEventListener('click', this.onTabClick));
    // Theme editor: selecting a block inside a filtered-out group would be
    // invisible — snap back to "All" so the merchant sees their selection.
    document.addEventListener('shopify:block:select', this.onBlockSelect);

    const initial = decodeURIComponent(window.location.hash.slice(1));
    if (initial && this.groups.some((group) => group.dataset.cgSeries === initial)) {
      this.applyFilter(initial, false);
    }
  }

  disconnectedCallback() {
    this.tabs?.forEach((tab) => tab.removeEventListener('click', this.onTabClick));
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
  }

  onTabClick = (event) => {
    this.applyFilter(event.currentTarget.dataset.cgFilter, true);
  };

  onBlockSelect = (event) => {
    if (!this.contains(event.target)) return;
    const group = event.target.closest('[data-cg-series]');
    if (group && group.hidden) this.applyFilter('all', false);
  };

  // Swatch click: mark it selected within its card, then swap in the
  // variant's pre-rendered price / image / URL from data attributes.
  onSwatchClick = (event) => {
    const swatch = event.target.closest('[data-cg-swatch]');
    if (!swatch) return;
    const card = swatch.closest('.cg-pricing__card');
    if (!card) return;

    card.querySelectorAll('[data-cg-swatch]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button === swatch));
    });

    if (swatch.dataset.cgPrice) {
      const price = card.querySelector('.cg-pricing__price');
      if (price) price.textContent = swatch.dataset.cgPrice;
    }

    if (swatch.dataset.cgImage) {
      const img = card.querySelector('.cg-pricing__media img');
      if (img) {
        img.src = swatch.dataset.cgImage;
        img.srcset = swatch.dataset.cgSrcset || '';
      }
    }

    if (swatch.dataset.cgVariantUrl) {
      card.querySelectorAll('a.cg-pricing__media, a.cg-pricing__details').forEach((link) => {
        link.href = swatch.dataset.cgVariantUrl;
      });
    }
  };

  applyFilter(filter, updateUrl) {
    this.groups.forEach((group) => {
      group.hidden = filter !== 'all' && group.dataset.cgSeries !== filter;
    });

    this.tabs.forEach((tab) => {
      const active = tab.dataset.cgFilter === filter;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-pressed', String(active));
      if (active && this.scroller && this.scroller.scrollWidth > this.scroller.clientWidth) {
        this.scroller.scrollTo({
          left: tab.offsetLeft - (this.scroller.clientWidth - tab.offsetWidth) / 2,
          behavior: 'smooth',
        });
      }
    });

    if (updateUrl) {
      const url = filter === 'all'
        ? window.location.pathname + window.location.search
        : '#' + encodeURIComponent(filter);
      window.history.replaceState(null, '', url);
    }
  }
}

if (!customElements.get('cg-pricing')) customElements.define('cg-pricing', CgPricing);
